import { Chart } from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ChartControls } from "./ChartControls";

// Register the annotation plugin
Chart.register(annotationPlugin);

interface ChartViewProps {
  dataColumns: string[];
  selectedPitches: Set<string>;
  pitches: Record<string, any>;
  movements: any[];
  currentTime: number;
  onTimelineChange: (time: number) => void;
}

export const ChartView: React.FC<ChartViewProps> = ({
  dataColumns,
  selectedPitches,
  pitches,
  movements,
  currentTime,
  onTimelineChange,
}) => {
  // Chart state
  const [selectedColumn, setSelectedColumn] = useState("");
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);

  // Chart refs
  const chartRef = useRef<Chart | null>(null);
  const chartCanvasRef = useRef<HTMLCanvasElement>(null);

  // Chart configuration constants
  const CHART_CONSTANTS = useMemo(
    () => ({
      TIMELINE_COLOR: "rgba(255, 99, 132, 0.8)",
      TIMELINE_WIDTH: 2,
      TIMELINE_DASH: [5, 5],
      DEFAULT_COLOR: "#FF6B6B",
      ALPHA_SUFFIX: "20",
    }),
    [],
  );

  const handleColumnSelectChange = useCallback((column: string) => {
    setSelectedColumn(column);
  }, []);

  const formatColumnName = useCallback((column: string) => {
    return column.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }, []);

  const getTimeFromPixel = useCallback((clientX: number) => {
    if (!chartRef.current || !chartCanvasRef.current) return undefined;

    const chart = chartRef.current;
    const canvas = chartCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;

    return chart.scales.x.getValueForPixel(x);
  }, []);

  const handleTimelineMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingTimeline(true);
  }, []);

  const handleTimelineMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDraggingTimeline) return;

      const newTime = getTimeFromPixel(e.clientX);
      if (newTime !== undefined) {
        onTimelineChange(newTime);
      }
    },
    [isDraggingTimeline, onTimelineChange, getTimeFromPixel],
  );

  const handleTimelineMouseUp = useCallback(() => {
    setIsDraggingTimeline(false);
  }, []);

  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isDraggingTimeline) return; // Don't handle click if dragging

      const clickedTime = getTimeFromPixel(e.clientX);
      if (clickedTime === undefined) return;

      // Set the current time to jump to that position
      onTimelineChange(clickedTime);
    },
    [isDraggingTimeline, onTimelineChange, getTimeFromPixel],
  );

  const destroyExistingChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  }, []);

  const createDataPoints = useCallback((pitch: any, column: string) => {
    return pitch.frames
      .filter((frame: any) => {
        const value = frame[column];
        return value !== null && value !== undefined && value !== "";
      })
      .map((frame: any) => ({
        x: frame.time_from_max_hand,
        y: frame[column],
      }));
  }, []);

  const createDataset = useCallback(
    (pitchId: string, dataPoints: any[], movementsList: any[]) => {
      const movement = movementsList.find((m) => m.id === pitchId);
      const color = movement?.color || CHART_CONSTANTS.DEFAULT_COLOR;

      return {
        label: `${movement?.movementNum} - ${movement?.pitchType}`,
        data: dataPoints,
        borderColor: color,
        backgroundColor: color + CHART_CONSTANTS.ALPHA_SUFFIX,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      };
    },
    [CHART_CONSTANTS],
  );

  const createChartOptions = useCallback(
    (datasets: any[], column: string, time: number) => {
      return {
        type: "line" as const,
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: "index" as const,
          },
          scales: {
            x: {
              type: "linear" as const,
              title: {
                display: true,
                text: "Time (s)",
              },
            },
            y: {
              title: {
                display: true,
                text: formatColumnName(column),
              },
            },
          },
          plugins: {
            legend: {
              display: true,
              position: "top" as const,
            },
            annotation: {
              annotations: {
                timeLine: {
                  type: "line",
                  xMin: time,
                  xMax: time,
                  borderColor: CHART_CONSTANTS.TIMELINE_COLOR,
                  borderWidth: CHART_CONSTANTS.TIMELINE_WIDTH,
                  borderDash: CHART_CONSTANTS.TIMELINE_DASH,
                  label: {
                    content: "Current Time",
                    enabled: true,
                    position: "start",
                  },
                },
              },
            },
          },
          onHover: (event: any, elements: any[]) => {
            const canvas = chartCanvasRef.current;
            if (canvas) {
              canvas.style.cursor = elements.length > 0 ? "pointer" : "grab";
            }
          },
        },
      } as any;
    },
    [formatColumnName, CHART_CONSTANTS],
  );

  const updateTimelinePosition = useCallback((chart: any, time: number) => {
    const timeLine = chart.options?.plugins?.annotation?.annotations?.timeLine;
    if (!timeLine) return;

    const chartData = chart.data.datasets[0]?.data;
    if (!chartData || chartData.length === 0) return;

    const xValues = chartData.map((point: any) => point.x);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const clampedTime = Math.max(minX, Math.min(maxX, time));

    timeLine.xMin = clampedTime;
    timeLine.xMax = clampedTime;
    chart.update("none");
  }, []);

  // Track the current column to detect when recreation is needed (use ref to avoid infinite loops)
  const currentChartColumnRef = useRef<string>("");

  const updateChart = useCallback(() => {
    if (!selectedColumn || selectedPitches.size === 0) {
      destroyExistingChart();
      currentChartColumnRef.current = "";
      return;
    }

    const canvas = chartCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Prepare datasets for selected pitches
    const datasets = Array.from(selectedPitches)
      .map((pitchId) => {
        const pitch = pitches[pitchId];
        if (!pitch) return null;

        const dataPoints = createDataPoints(pitch, selectedColumn);
        return dataPoints.length > 0
          ? createDataset(pitchId, dataPoints, movements)
          : null;
      })
      .filter((dataset): dataset is any => dataset !== null);

    if (datasets.length === 0) {
      destroyExistingChart();
      currentChartColumnRef.current = "";
      return;
    }

    // Only recreate chart if it doesn't exist or column changed
    if (!chartRef.current || currentChartColumnRef.current !== selectedColumn) {
      destroyExistingChart();
      const chartOptions = createChartOptions(
        datasets,
        selectedColumn,
        currentTime,
      );
      chartRef.current = new Chart(ctx, chartOptions);
      currentChartColumnRef.current = selectedColumn;
    } else {
      // Update existing chart data
      // Double-check that chart still exists (race condition protection)
      if (!chartRef.current) {
        // Chart was destroyed, recreate it
        const chartOptions = createChartOptions(
          datasets,
          selectedColumn,
          currentTime,
        );
        chartRef.current = new Chart(ctx, chartOptions);
        currentChartColumnRef.current = selectedColumn;
      } else {
        // Safe to update existing chart
        chartRef.current.data.datasets = datasets;

        // Update the Y-axis title if it changed
        if (
          chartRef.current.options.scales?.y &&
          "title" in chartRef.current.options.scales.y &&
          chartRef.current.options.scales.y.title
        ) {
          chartRef.current.options.scales.y.title.text =
            formatColumnName(selectedColumn);
        }

        // Use Chart.js update method instead of recreation
        try {
          chartRef.current.update("none");
        } catch (error) {
          console.warn("Chart update failed, recreating chart:", error);
          // If update fails, recreate the chart
          destroyExistingChart();
          const chartOptions = createChartOptions(
            datasets,
            selectedColumn,
            currentTime,
          );
          chartRef.current = new Chart(ctx, chartOptions);
          currentChartColumnRef.current = selectedColumn;
        }
      }
    }

    // Update timeline position
    if (chartRef.current) {
      updateTimelinePosition(chartRef.current, currentTime);
    }
  }, [
    selectedColumn,
    selectedPitches,
    pitches,
    movements,
    currentTime,
    destroyExistingChart,
    createDataPoints,
    createDataset,
    createChartOptions,
    updateTimelinePosition,
    formatColumnName,
  ]);

  // Update chart when dependencies change
  useEffect(() => {
    updateChart();
  }, [updateChart]);

  // Update chart timeline annotation when currentTime changes
  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current as any;
      if (chart.options?.plugins?.annotation?.annotations?.timeLine) {
        const timeLine = chart.options.plugins.annotation.annotations.timeLine;

        // Only update timeline if currentTime is within the chart's data range
        const chartData = chart.data.datasets[0]?.data;
        if (chartData && chartData.length > 0) {
          const minX = Math.min(...chartData.map((point: any) => point.x));
          const maxX = Math.max(...chartData.map((point: any) => point.x));

          // Clamp currentTime to the actual data range
          const clampedTime = Math.max(minX, Math.min(maxX, currentTime));

          timeLine.xMin = clampedTime;
          timeLine.xMax = clampedTime;
          chart.update("none");
        }
      }
    }
  }, [currentTime]);

  // Cleanup chart on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  return (
    <div
      className="bg-white p-4 overflow-hidden flex flex-col h-full"
      data-testid="chart-container"
    >
      <ChartControls
        selectedColumn={selectedColumn}
        dataColumns={dataColumns}
        chartRef={chartRef}
        onColumnChange={handleColumnSelectChange}
      />
      {!selectedColumn || selectedPitches.size === 0 ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
          {!selectedColumn ? (
            <div className="text-center" data-testid="chart-message">
              <div className="mb-1">📊 Select a chart column above</div>
              <div className="text-xs">Choose a data column to visualize</div>
            </div>
          ) : (
            <div
              className="text-center"
              data-testid="chart-message-no-movements"
            >
              <div className="mb-1">🎯 Select movements to view</div>
              <div className="text-xs">
                Click on movements in the sidebar to see data
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 relative" data-testid="chart-area">
          <canvas
            ref={chartCanvasRef}
            id="chartCanvas"
            className={`w-full h-full ${
              isDraggingTimeline ? "cursor-grabbing" : "cursor-grab"
            }`}
            data-testid="chart-canvas"
            onMouseDown={handleTimelineMouseDown}
            onMouseMove={handleTimelineMouseMove}
            onMouseUp={handleTimelineMouseUp}
            onClick={handleTimelineClick}
          />
        </div>
      )}
    </div>
  );
};
